import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const CommunityImpact = () => {
  const impactAreas = [
  {
    title: "Local Partnerships",
    description: "We source ingredients from Ontario farms whenever possible, supporting local agriculture while maintaining quality. Our coffee comes from a Toronto roaster, our dairy from a family farm in Caledon, and our honey from Etobicoke beekeepers.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1102b95a2-1764538642691.png",
    imageAlt: "Friendly handshake between bakery owner and local farmer in front of delivery truck loaded with fresh produce and dairy products, sunny morning light highlighting partnership and community connection in rural Ontario setting",
    icon: "Handshake",
    stats: [
    { value: "12+", label: "Local Suppliers" },
    { value: "100%", label: "Ontario Dairy" },
    { value: "Weekly", label: "Fresh Deliveries" }]

  },
  {
    title: "Community Events",
    description: "Every month, we host Italian baking workshops where customers learn traditional techniques. We sponsor local youth sports teams, donate to school fundraisers, and provide pastries for community gatherings. During holidays, we organize Italian cultural celebrations that bring the neighborhood together.",
    image: "https://images.unsplash.com/photo-1562663648-008ed8b13f61",
    imageAlt: "Diverse group of smiling community members of all ages gathered around bakery table during Italian baking workshop, hands covered in flour while learning to shape dough, warm festive atmosphere with decorations and fresh pastries",
    icon: "Users",
    stats: [
    { value: "24+", label: "Annual Events" },
    { value: "500+", label: "Workshop Attendees" },
    { value: "15+", label: "Teams Sponsored" }]

  },
  {
    title: "Charitable Giving",
    description: "We believe in giving back to the community that has supported us for 40 years. Every day, unsold products go to local shelters and food banks. We provide free birthday cakes for children in need and offer discounted catering for non-profit events. Our 'Pay It Forward' program allows customers to pre-purchase coffee and pastries for those experiencing hardship.",
    image: "https://images.unsplash.com/photo-1582885466324-a394b90d77d2",
    imageAlt: "Bakery staff members loading boxes of fresh bread and pastries into community food bank van, volunteers smiling while organizing donations, showing compassionate community service and charitable giving in action",
    icon: "Heart",
    stats: [
    { value: "2000+", label: "Meals Donated" },
    { value: "50+", label: "Free Birthday Cakes" },
    { value: "$25K+", label: "Annual Giving" }]

  },
  {
    title: "Cultural Bridge",
    description: "As an Italian family in Canada, we're proud to share our heritage with our diverse neighborhood. We celebrate both Italian and Canadian holidays, teach Italian baking traditions to new generations, and create a space where different cultures connect over authentic food. Many customers tell us we're their first taste of real Italian culture.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a5a7ba0a-1764538652938.png",
    imageAlt: "Multicultural group of customers from diverse backgrounds enjoying Italian pastries and coffee together at bakery cafe tables, engaged in friendly conversation, Italian flag and Canadian flag displayed on wall symbolizing cultural bridge",
    icon: "Globe",
    stats: [
    { value: "30+", label: "Nationalities Served" },
    { value: "8", label: "Cultural Events" },
    { value: "40", label: "Years of Heritage" }]

  }];


  const testimonials = [
  {
    quote: "Montesanto isn't just a bakery—it's the heart of our neighborhood. They've been part of every celebration, every milestone, every Sunday morning for as long as I can remember.",
    author: "Sarah Chen",
    role: "20-Year Customer",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_128d1f30b-1763296787936.png",
    avatarAlt: "Professional headshot of Asian woman with shoulder-length black hair wearing blue blazer and warm smile against neutral background"
  },
  {
    quote: "When my restaurant needed a reliable supplier for authentic Italian bread, Montesanto was the obvious choice. Their consistency and quality are unmatched in Toronto.",
    author: "Marco Rossi",
    role: "Restaurant Owner",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1846958dc-1763299616060.png",
    avatarAlt: "Professional headshot of Italian man with short dark hair and beard wearing chef\'s whites and confident smile in restaurant kitchen setting"
  },
  {
    quote: "They sponsored our daughter\'s soccer team and provided pastries for every game. That\'s the kind of community support that makes a neighborhood special.",
    author: "Jennifer Williams",
    role: "Community Member",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1cab38a1c-1763294638276.png",
    avatarAlt: "Professional headshot of African American woman with natural curly hair wearing casual sweater and genuine smile against outdoor background"
  }];


  return (
    <section className="py-20 bg-gradient-to-b from-background to-card">
      <div className="container-custom">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
            <Icon name="Heart" size={18} color="var(--color-primary)" />
            <span className="text-sm font-medium text-primary">Community Connection</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-foreground mb-4">
            Our Impact on Etobicoke
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            For 40 years, we've been more than a bakery—we're a gathering place, a cultural bridge, and a committed community partner
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-20">
          {impactAreas?.map((area, index) =>
          <div key={index} className="bg-background rounded-2xl shadow-warm overflow-hidden group">
              <div className="relative aspect-[16/9] overflow-hidden">
                <Image
                src={area?.image}
                alt={area?.imageAlt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6">
                  <div className="w-14 h-14 rounded-xl bg-background/90 backdrop-blur-sm flex items-center justify-center">
                    <Icon name={area?.icon} size={28} color="var(--color-primary)" />
                  </div>
                </div>
              </div>

              <div className="p-8">
                <h3 className="font-serif text-2xl font-bold text-foreground mb-3">
                  {area?.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  {area?.description}
                </p>

                <div className="grid grid-cols-3 gap-4">
                  {area?.stats?.map((stat, idx) =>
                <div key={idx} className="text-center">
                      <div className="font-accent text-3xl font-bold text-primary mb-1">
                        {stat?.value}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {stat?.label}
                      </div>
                    </div>
                )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-accent/10 via-primary/5 to-accent/10 rounded-2xl p-8 md:p-12">
          <div className="text-center mb-12">
            <h3 className="font-serif text-3xl font-bold text-foreground mb-4">
              What Our Community Says
            </h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              These testimonials represent thousands of relationships built over four decades of serving Etobicoke
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials?.map((testimonial, index) =>
            <div key={index} className="bg-background rounded-xl p-6 shadow-warm">
                <div className="flex items-center gap-4 mb-4">
                  <div className="relative w-14 h-14 rounded-full overflow-hidden flex-shrink-0">
                    <Image
                    src={testimonial?.avatar}
                    alt={testimonial?.avatarAlt}
                    className="w-full h-full object-cover" />

                  </div>
                  <div>
                    <div className="font-semibold text-foreground">
                      {testimonial?.author}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {testimonial?.role}
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <Icon name="Quote" size={24} color="var(--color-primary)" className="opacity-50" />
                </div>

                <p className="text-muted-foreground leading-relaxed italic">
                  "{testimonial?.quote}"
                </p>

                <div className="flex gap-1 mt-4">
                  {[...Array(5)]?.map((_, i) =>
                <Icon key={i} name="Star" size={16} color="var(--color-accent)" fill="var(--color-accent)" />
                )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-16 text-center">
          <div className="inline-flex flex-col items-center gap-4 px-8 py-6 rounded-2xl bg-primary/10 border border-primary/20">
            <Icon name="Heart" size={48} color="var(--color-primary)" />
            <div>
              <h4 className="font-serif text-2xl font-bold text-foreground mb-2">
                Join Our Community
              </h4>
              <p className="text-muted-foreground mb-4">
                Whether you're a long-time customer or visiting for the first time, you're part of our extended famiglia
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-background text-sm">
                  <Icon name="Users" size={16} color="var(--color-primary)" />
                  <span className="text-foreground font-medium">10,000+ Customers</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-background text-sm">
                  <Icon name="Award" size={16} color="var(--color-primary)" />
                  <span className="text-foreground font-medium">4.7 Star Rating</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-background text-sm">
                  <Icon name="Clock" size={16} color="var(--color-primary)" />
                  <span className="text-foreground font-medium">40 Years Strong</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default CommunityImpact;